import { FirstHeatToggle } from '@/components/FirstHeatToggle';
import { useUserProfile } from '@/hooks/useUserData';
import { useReservationStore } from '@/store/reservation/reservationStore';
import {
    buildReservationDayEligibilityStatuses,
    getCartDailyWaveUsage,
    mergeDailyWaveUsages,
    type ReservationDayEligibilityStatus,
} from '@/store/reservation/reservationStore';
import {
    getAvailableGifts,
    useShoppingCartStore,
} from '@/store/shoppingCart/shoppingCartStore';
import { FirstHeatNotice } from '@/templates/ModalShopping/pieces/FirstHeatNotice';
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { LuChevronLeft, LuChevronRight, LuTicket, LuX } from 'react-icons/lu';
import { toast } from 'react-toastify';
import {
    MdFlashOn,
    MdOutlineInfo,
    MdWarningAmber,
    MdShoppingCart,
    MdSurfing,
} from 'react-icons/md';
import { isUndecidedDate } from '../../components/Calendar';
import CalendarButton from '../../components/CalendarButton';
import { Dropdown } from '../../components/Dropdown';
import { ItemsCartSmallScreen } from '../../components/ItemsCartSmallScreen';
import { Modal } from '../../components/Modal';
import { AddingAdvancedModal } from '../../components/ModalAddingAdvanced';
import { default as ReactIcon } from '../../components/ReactIcon';
import { SidebarCardShop } from '../../components/SidebarCardShop';
import { Loading } from '../../components/SurfingLoading';
import {
    WithoutElementsShop,
    WithoutElementsShopFirstHeat,
} from '../../components/WithoutElementsShop';
import { Render } from '../../helpers/Render';
import { SectionsEnum, sectionsConst, WaveLevelEnum } from '../../helpers/utils';
import { useToken } from '../../store/auth/tokenStore';
import { useModalShoppingLogic } from './logic/useModalShoppingLogic';
import { CardSchedule } from './pieces/CardSchedule';
import { CardShop } from './pieces/CardShop';
import { SideWave } from './pieces/SideWave';
import { featureFlags } from '../../config/featureFlags';
import { useBannerStore } from '../../store/ui/bannerStore';
import PopupAgostoBanner from '@/assets/banner/popup-agosto.png';
import { DateUtils } from '@/helpers/dateUtils';
import {
    canPurchaseStoreDate,
    getStorePublicMaxDate,
    isStoreOpenByDate,
    STORE_RESERVATION_REQUIRED_MESSAGE,
    STORE_RESERVATION_WINDOW_MESSAGE,
} from '@/helpers/storeAccess';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    HeaderFilters,
    ModalFilterHeaderWrap,
    ModalShoppingWrapper,
    ReservationArrowButton,
    ReservationDayButton,
    ReservationDayButtonContent,
    ReservationDayCarouselWrap,
    ReservationDayCounter,
    ReservationDayDetailItem,
    ReservationDayDetailsPanel,
    ReservationDayDot,
    ReservationDayDots,
    ReservationDayLabel,
    ReservationDaySoldOutAlert,
    ReservationDayRuler,
    ReservationDaysList,
    ShopValuesWrap,
    TextWarn,
    WarnShoppingTextIcon,
    WrapElementsSmallBig,
    WrapFilterDropdownBadge,
    WrapIconCartArrow,
    WrapperShoppingModal,
} from './styles';

const LoginPriceNotice = (
    <div className="">
        <WarnShoppingTextIcon>
            <MdOutlineInfo size={'1.5rem'} color="#453B1E" />
            <TextWarn>
                Preços com base no perfil público geral. Proprietário(a):
                realize login para atualizar os valores.
            </TextWarn>
        </WarnShoppingTextIcon>
    </div>
);
export const ModalShopping: React.FC = () => {
    const { token } = useToken();
    const navigate = useNavigate();
    const location = useLocation();
    // Separando toda a logica e funções para um hook
    const {
        wavesData,
        isPending,
        currentSwim,
        matchQuery,
        isWarnVisible,
        modalShoppingIsVisible,
        cartIsVisible,
        directionWave,
        waveFiltered,
        elementsWavesExhibitionName,
        currentDate,
        setCartIsVisible,
        setIsWarnVisible,
        handleAddToCart,
        setCurrentSectionHandle,
        setCurrentSwimHandle,
        setDirectionWaveHandle,
        setModalIsVisible,
        setCurrentDate,
        isFirstHeat,
        setFirstHeat,
    } = useModalShoppingLogic();
    const userProfile = useUserProfile();
    const activeReservation = useReservationStore(state => state.activeReservation);
    const cartValues = useShoppingCartStore(state => state.cartValues);
    const cleanCart = useShoppingCartStore(state => state.cleanCart);
    const clearActiveReservation = useReservationStore(
        state => state.clearActiveReservation
    );

    const shouldShowScrollItems = !!wavesData && wavesData?.length > 0;
    const canBuySelectedDate = canPurchaseStoreDate({
        purchaseDate: currentDate,
        activeReservation,
    });
    const publicStoreMaxDate = useMemo(() => getStorePublicMaxDate(), []);

    const handleAddToCartWithReservation = (
        ...args: Parameters<typeof handleAddToCart>
    ) => {
        const [cartItem] = args;
        if (
            !canPurchaseStoreDate({
                purchaseDate: cartItem.date,
                activeReservation,
            })
        ) {
            toast.warning(
                activeReservation
                    ? STORE_RESERVATION_WINDOW_MESSAGE
                    : STORE_RESERVATION_REQUIRED_MESSAGE
            );
            return;
        }
        handleAddToCart(...args);
    };

    const DisabledNotice = (
        <WrapperShoppingModal>
            <WarnShoppingTextIcon>
                <MdOutlineInfo size={'1.5rem'} color="#453B1E" />
                <TextWarn>
                    Fique atento aos produtos com o destaque em{' '}
                    {<ReactIcon Icon={MdFlashOn} />}! Isso indica que você está
                    vendo o melhor preço da sessão para o dia de hoje.
                    Garantimos que esses valores representam as ofertas mais
                    vantajosas que selecionamos cuidadosamente para você.
                    Aproveite!
                </TextWarn>
                <LuX
                    size={'1.5rem'}
                    cursor={'pointer'}
                    onClick={() => setIsWarnVisible(false)}
                />
            </WarnShoppingTextIcon>
        </WrapperShoppingModal>
    );

    const userAvailableGifts = useMemo(() => {
        if (!userProfile.data) return [];
        if (!userProfile.data.gifts?.length) return [];
        const notYetUsedGifts = getAvailableGifts(userProfile.data);

        return notYetUsedGifts;
    }, [userProfile.data]);

    const reservationRange = useMemo(() => {
        if (!activeReservation) return null;
        return {
            minDate: activeReservation.checkIn,
            maxDate: activeReservation.checkOut,
        };
    }, [activeReservation]);

    const reservationDays = useMemo(() => {
        if (!activeReservation) return [];

        const days: Date[] = [];
        const cursor = moment(activeReservation.checkIn).startOf('day');
        const end = moment(activeReservation.checkOut).startOf('day');

        while (cursor.isSameOrBefore(end, 'day')) {
            days.push(cursor.toDate());
            cursor.add(1, 'day');
        }

        return days;
    }, [activeReservation]);

    const selectedReservationDayIndex = useMemo(() => {
        if (!activeReservation || reservationDays.length === 0) return -1;
        return reservationDays.findIndex(day =>
            DateUtils.areDatesEqual(day, currentDate)
        );
    }, [activeReservation, reservationDays, currentDate]);

    const CAROUSEL_WINDOW_SIZE = 4;
    const [windowStart, setWindowStart] = useState(0);

    // Reseta a janela quando a reserva muda
    useEffect(() => {
        setWindowStart(0);
    }, [activeReservation]);

    // Mantém o dia selecionado visível na janela do carousel (mobile)
    useEffect(() => {
        if (selectedReservationDayIndex < 0 || matchQuery) return;
        setWindowStart(prev => {
            if (selectedReservationDayIndex < prev) return selectedReservationDayIndex;
            if (selectedReservationDayIndex >= prev + CAROUSEL_WINDOW_SIZE) {
                return selectedReservationDayIndex - CAROUSEL_WINDOW_SIZE + 1;
            }
            return prev;
        });
    }, [selectedReservationDayIndex, matchQuery]);

    // Mobile: mostra janela de 5 dias. Desktop: mostra todos
    const visibleDays = useMemo(() => {
        if (matchQuery) return reservationDays;
        return reservationDays.slice(windowStart, windowStart + CAROUSEL_WINDOW_SIZE);
    }, [reservationDays, windowStart, matchQuery]);

    const reservationDayStatuses = useMemo(() => {
        if (!activeReservation) return [];
        const cartUsage = getCartDailyWaveUsage(cartValues || []);
        const mergedUsage = mergeDailyWaveUsages(
            activeReservation.dailyWaveUsage,
            cartUsage
        );
        return buildReservationDayEligibilityStatuses(mergedUsage);
    }, [activeReservation, cartValues]);

    const reservationDayStatusMap = useMemo(
        () =>
            new Map(
                reservationDayStatuses.map(status => [status.dateKey, status] as const)
            ),
        [reservationDayStatuses]
    );

    const getDayStatus = (day: Date): ReservationDayEligibilityStatus => {
        const dateKey = DateUtils.formatToInitalYear(day);
        return (
            reservationDayStatusMap.get(dateKey) || {
                dateKey,
                freeReefStatus: 'available',
                freeBayStatus: 'available',
                paidReefStatus: 'available',
            }
        );
    };

    const getDayOverallStatus = (status: ReservationDayEligibilityStatus): 'available' | 'partial' | 'exhausted' => {
        const statuses = [status.freeReefStatus, status.freeBayStatus, status.paidReefStatus];
        const availableCount = statuses.filter(s => s === 'available').length;
        if (availableCount === statuses.length) return 'available';
        if (availableCount === 0) return 'exhausted';
        return 'partial';
    };

    const selectedDayStatus = useMemo(() => {
        if (!activeReservation) return null;
        return getDayStatus(currentDate);
    }, [activeReservation, currentDate, reservationDayStatusMap]);

    const isWaveVisibleForReservation = useMemo(() => {
        if (!activeReservation || !selectedDayStatus) return (_waveLevel: WaveLevelEnum | undefined) => true;
        return (waveLevel: WaveLevelEnum | undefined): boolean => {
            if (waveLevel === WaveLevelEnum.BAY) {
                return selectedDayStatus.freeBayStatus === 'available';
            }
            if (waveLevel === WaveLevelEnum.REEF) {
                return (
                    selectedDayStatus.freeReefStatus === 'available' ||
                    selectedDayStatus.paidReefStatus === 'available'
                );
            }
            return true;
        };
    }, [activeReservation, selectedDayStatus]);

    const isSelectedDayFullyExhausted = useMemo(() => {
        if (!selectedDayStatus) return false;
        return (
            selectedDayStatus.freeReefStatus === 'exhausted' &&
            selectedDayStatus.freeBayStatus === 'exhausted' &&
            selectedDayStatus.paidReefStatus === 'exhausted'
        );
    }, [selectedDayStatus]);

    const hasWaveAvailabilityForSelectedDay = useMemo(() => {
        if (!activeReservation) return true;

        if (waveFiltered) {
            return (
                isWaveVisibleForReservation(waveFiltered.waveLevel) &&
                waveFiltered.schedules.length > 0
            );
        }

        return (wavesData ?? [])
            .filter(item => isWaveVisibleForReservation(item.waveLevel))
            .some(item => item.schedules.length > 0);
    }, [activeReservation, waveFiltered, wavesData, isWaveVisibleForReservation]);

    useEffect(() => {
        if (userAvailableGifts.length === 0 && isFirstHeat) {
            setFirstHeat(false);
            return;
        }
    }, [userAvailableGifts, isFirstHeat, setFirstHeat]);

    useEffect(() => {
        if (activeReservation && isFirstHeat) {
            setFirstHeat(false);
        }
    }, [activeReservation, isFirstHeat, setFirstHeat]);

    useEffect(() => {
        if (!activeReservation) return;
        if (currentDate < activeReservation.checkIn) {
            setCurrentDate(activeReservation.checkIn);
            return;
        }
        if (currentDate > activeReservation.checkOut) {
            setCurrentDate(activeReservation.checkOut);
        }
    }, [activeReservation, currentDate, setCurrentDate]);

    useEffect(() => {
        if (activeReservation) return;
        if (!isStoreOpenByDate(currentDate)) {
            setCurrentDate(publicStoreMaxDate);
        }
    }, [activeReservation, currentDate, publicStoreMaxDate, setCurrentDate]);

    const handleSelectPreviousReservationDay = () => {
        if (selectedReservationDayIndex <= 0) return;
        setCurrentDate(reservationDays[selectedReservationDayIndex - 1]);
    };

    const handleSelectNextReservationDay = () => {
        if (selectedReservationDayIndex < 0) return;
        if (selectedReservationDayIndex >= reservationDays.length - 1) return;
        setCurrentDate(reservationDays[selectedReservationDayIndex + 1]);
    };

    const handleSetModalVisibility = (visibility?: boolean) => {
        const nextVisibility =
            visibility !== undefined ? visibility : !modalShoppingIsVisible;

        if (!nextVisibility) {
            setCartIsVisible(false);
        }

        if (!nextVisibility && (activeReservation || location.pathname.startsWith('/reserva'))) {
            clearActiveReservation();
            cleanCart();
            navigate('/', { replace: true });
        }

        setModalIsVisible(nextVisibility);
    };

    // Mostra o banner na toda a vez que abre o carrinho
    useEffect(() => {
        if (
            featureFlags.showLoginBanner &&
            modalShoppingIsVisible &&
            (
                userProfile.data?.accessProfile.name === 'OpenSea' ||
                userProfile.data?.accessProfile.name === 'OpenSeaPlus' ||
                userProfile.data?.accessProfile.name === 'Guest'
            )
         ) {
            useBannerStore.getState().showBanner({
                imageSrc: PopupAgostoBanner,
                alt: 'Banner de agosto',
            });
        }
    }, [token, modalShoppingIsVisible]);

    return (
        <Modal
            title="Conheça nossas ondas"
            showModal={modalShoppingIsVisible}
            setShowModal={handleSetModalVisibility}
            stylesChildren={{
                // overflow: 'hidden',
                // height: '100%',
                flexGrow: 1,
                overflow: 'hidden',
            }}
        >
            {isPending && !wavesData?.length && <Loading />}
            <WrapElementsSmallBig isBigScreen={matchQuery}>
                <ModalShoppingWrapper cartMobileIsOpen={cartIsVisible}>
                    <HeaderFilters>
                        <ModalFilterHeaderWrap>
                            <WrapFilterDropdownBadge>
                                <Dropdown
                                    setCurrentSelected={setCurrentSectionHandle}
                                    title={SectionsEnum.SECTIONS}
                                    elements={sectionsConst.map(item => ({
                                        id: item,
                                        value: item,
                                    }))}
                                    allSize={!matchQuery}
                                />
                                {(userAvailableGifts.length ?? 0) > 0 &&
                                    !activeReservation && (
                                    <FirstHeatToggle
                                        isFirstHeat={isFirstHeat}
                                        setFirstHeat={setFirstHeat}
                                    />
                                )}
                                <Dropdown
                                    currentSelected={currentSwim}
                                    setCurrentSelected={setCurrentSwimHandle}
                                    title="Ver tudo"
                                    elements={elementsWavesExhibitionName}
                                    allSize={!matchQuery}
                                />
                            </WrapFilterDropdownBadge>
                            <WrapFilterDropdownBadge>
                                <div className="wave-side-control">
                                    <SideWave
                                        direction={directionWave}
                                        onSelect={setDirectionWaveHandle}
                                    />
                                </div>
                                {activeReservation && reservationRange ? (
                                    <ReservationDayCarouselWrap>
                                        <ReservationDayRuler>
                                            <ReservationArrowButton
                                                className="reservation-arrow"
                                                onClick={handleSelectPreviousReservationDay}
                                                disabled={selectedReservationDayIndex <= 0}
                                                aria-label="Dia anterior"
                                            >
                                                <LuChevronLeft size={'1rem'} />
                                            </ReservationArrowButton>
                                            <ReservationDaysList>
                                                {visibleDays.map(day => {
                                                    const dateKey = DateUtils.formatToInitalYear(day);
                                                    const dayStatus = getDayStatus(day);
                                                    const overallStatus = getDayOverallStatus(dayStatus);

                                                    return (
                                                        <ReservationDayButton
                                                            className="reservation-day"
                                                            key={dateKey}
                                                            $dayStatus={overallStatus}
                                                            aria-selected={DateUtils.areDatesEqual(day, currentDate)}
                                                            onClick={() => setCurrentDate(day)}
                                                        >
                                                            <ReservationDayButtonContent>
                                                                <ReservationDayLabel>
                                                                    {DateUtils.formatToDayMonth(day)}
                                                                </ReservationDayLabel>
                                                            </ReservationDayButtonContent>
                                                        </ReservationDayButton>
                                                    );
                                                })}
                                            </ReservationDaysList>
                                            <ReservationArrowButton
                                                className="reservation-arrow"
                                                onClick={handleSelectNextReservationDay}
                                                disabled={
                                                    selectedReservationDayIndex < 0 ||
                                                    selectedReservationDayIndex >=
                                                        reservationDays.length - 1
                                                }
                                                aria-label="Próximo dia"
                                            >
                                                <LuChevronRight size={'1rem'} />
                                            </ReservationArrowButton>
                                        </ReservationDayRuler>

                                        {selectedDayStatus && (
                                            <ReservationDayDetailsPanel $visible={true}>
                                                <ReservationDayDetailItem $status={selectedDayStatus.freeReefStatus}>
                                                    REEF Grátis — {selectedDayStatus.freeReefStatus === 'available' ? 'Disponível' : 'Esgotado'}
                                                </ReservationDayDetailItem>
                                                <ReservationDayDetailItem $status={selectedDayStatus.freeBayStatus}>
                                                    BAY Grátis — {selectedDayStatus.freeBayStatus === 'available' ? 'Disponível' : 'Esgotado'}
                                                </ReservationDayDetailItem>
                                                <ReservationDayDetailItem $status={selectedDayStatus.paidReefStatus}>
                                                    REEF Pago — {selectedDayStatus.paidReefStatus === 'available' ? 'Disponível' : 'Esgotado'}
                                                </ReservationDayDetailItem>
                                            </ReservationDayDetailsPanel>
                                        )}

                                        {!matchQuery && reservationDays.length > CAROUSEL_WINDOW_SIZE && (
                                            reservationDays.length <= 14 ? (
                                                <ReservationDayDots>
                                                    {reservationDays.map((day, i) => {
                                                        const s = getDayStatus(day);
                                                        return (
                                                            <ReservationDayDot
                                                                key={i}
                                                                $active={i === selectedReservationDayIndex}
                                                                $dayStatus={getDayOverallStatus(s)}
                                                            />
                                                        );
                                                    })}
                                                </ReservationDayDots>
                                            ) : (
                                                <ReservationDayCounter>
                                                    {selectedReservationDayIndex + 1} / {reservationDays.length}
                                                </ReservationDayCounter>
                                            )
                                        )}
                                    </ReservationDayCarouselWrap>
                                ) : (
                                    <>
                                        <CalendarButton
                                            defaultDate={currentDate}
                                            onDateChange={date => {
                                                if (!date) return;
                                                if (
                                                    !canPurchaseStoreDate({
                                                        purchaseDate: date,
                                                        activeReservation,
                                                    })
                                                ) {
                                                    toast.warning(
                                                        activeReservation
                                                            ? STORE_RESERVATION_WINDOW_MESSAGE
                                                            : STORE_RESERVATION_REQUIRED_MESSAGE
                                                    );
                                                    return;
                                                }
                                                setCurrentDate(date);
                                            }}
                                            textInput={matchQuery ? undefined : 'Data'}
                                            pricesProductId={waveFiltered?.id?.toString()}
                                            shouldAllowUndecidedDate={false}
                                            maxDate={publicStoreMaxDate}
                                        />
                                    </>
                                )}
                            </WrapFilterDropdownBadge>
                        </ModalFilterHeaderWrap>
                        <WrapIconCartArrow
                            isSmallScreen={!matchQuery}
                            onClick={() => setCartIsVisible(!cartIsVisible)}
                        >
                            <MdShoppingCart size={'1.5rem'} />
                            {cartIsVisible && <LuChevronLeft size={'1.5rem'} />}
                            {!cartIsVisible && (
                                <LuChevronRight size={'1.5rem'} />
                            )}
                        </WrapIconCartArrow>
                    </HeaderFilters>

                    {shouldShowScrollItems && canBuySelectedDate && (
                        // <ScrollWrap>
                        <ShopValuesWrap>
                            {isSelectedDayFullyExhausted && (
                                <ReservationDaySoldOutAlert>
                                    <MdWarningAmber size={'2.25rem'} />
                                    Reserva esgotada para esse dia
                                </ReservationDaySoldOutAlert>
                            )}
                            {!isSelectedDayFullyExhausted &&
                                activeReservation &&
                                !hasWaveAvailabilityForSelectedDay && (
                                <ReservationDaySoldOutAlert>
                                    <MdOutlineInfo size={'2.25rem'} />
                                    Sem disponibilidade de ondas para esse dia
                                </ReservationDaySoldOutAlert>
                            )}
                            {!token && LoginPriceNotice}
                            {/* Desabilitado temporariamente */}
                            {false &&
                                matchQuery &&
                                isWarnVisible &&
                                DisabledNotice}
                            {isFirstHeat && (
                                <FirstHeatNotice
                                    firstHeatCount={userAvailableGifts.length}
                                />
                            )}
                            {waveFiltered
                                ? isWaveVisibleForReservation(waveFiltered.waveLevel)
                                    ? waveFiltered.schedules.map(schedule => (
                                          <CardSchedule
                                              key={schedule.id}
                                              itemShop={waveFiltered}
                                              schedule={schedule}
                                              date={currentDate}
                                              handleAddToCart={
                                                  handleAddToCartWithReservation
                                              }
                                              isUndecidedDate={isUndecidedDate(
                                                  currentDate
                                              )}
                                              isFirstHeat={isFirstHeat}
                                              currentGift={userAvailableGifts[0]}
                                              reservationDayStatus={activeReservation ? getDayStatus(currentDate) : undefined}
                                          />
                                      ))
                                    : null
                                : wavesData
                                      .filter(item => isWaveVisibleForReservation(item.waveLevel))
                                      .map((item) => (
                                          <CardShop
                                              key={item.id}
                                              date={currentDate}
                                              handleAddToCart={
                                                  handleAddToCartWithReservation
                                              }
                                              itemShop={item}
                                              isUndecidedDate={isUndecidedDate(
                                                  currentDate
                                              )}
                                              isFirstHeat={isFirstHeat}
                                              currentGift={userAvailableGifts[0]}
                                              reservationDayStatus={activeReservation ? getDayStatus(currentDate) : undefined}
                                          />
                                      ))}
                        </ShopValuesWrap>
                        // </ScrollWrap>
                    )}
                    {!canBuySelectedDate && !isFirstHeat && (
                        <ShopValuesWrap>
                            <WithoutElementsShop
                                icon={MdSurfing}
                                title={
                                    activeReservation
                                        ? 'Com reserva, você só pode comprar dentro do período do check-in ao check-out.'
                                        : STORE_RESERVATION_REQUIRED_MESSAGE
                                }
                            />
                        </ShopValuesWrap>
                    )}
                    {!shouldShowScrollItems && canBuySelectedDate && !isFirstHeat && (
                        <ShopValuesWrap>
                            <WithoutElementsShop
                                icon={MdSurfing}
                                title="Não temos horários disponíveis para o tipo de onda selecionado."
                            />
                        </ShopValuesWrap>
                    )}

                    {!shouldShowScrollItems && isFirstHeat && (
                        <ShopValuesWrap>
                            <WithoutElementsShopFirstHeat
                                icon={LuTicket}
                                title="Não há bônus disponíveis para agendamento nesta data."
                            />
                        </ShopValuesWrap>
                    )}
                    <Render show={!matchQuery}>
                        <ItemsCartSmallScreen
                            shopVisible={cartIsVisible}
                            setShopVisible={() =>
                                setCartIsVisible(!cartIsVisible)
                            }
                        />
                    </Render>
                </ModalShoppingWrapper>

                <Render show={matchQuery}>
                    <SidebarCardShop />
                </Render>
            </WrapElementsSmallBig>

            <AddingAdvancedModal />
        </Modal>
    );
};
